import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import {
    map,
    Observable,
    of,
    retry,
    switchMap,
    tap,
    throwError,
    timer
} from "rxjs";
import { environment } from "~environments/environment";

export type ReportEmbedInfo = {
    embedUrl: string;
    accessToken: string;
};

@Injectable({
    providedIn: "root"
})
export class ReportEmbedService {
    private readonly maxThrottleRetries = 3;
    private readonly throttleDelay = 1000;

    private readonly msalService = inject(MsalService);
    private readonly httpClient = inject(HttpClient);

    private readonly tokenCache = new Map<string, ReportEmbedInfo>();

    getTokenAndUrl(reportId: string) {
        return this.msalService.acquireTokenSilent({
            scopes: environment.powerBIConfig.scopes
        }).pipe(
            switchMap((token) => this.getReportEmbedUrl(reportId, token.accessToken)),
            tap((embedInfo) => this.tokenCache.set(reportId, embedInfo))
        );
    }

    private getReportEmbedUrl(reportId: string, accessToken: string): Observable<ReportEmbedInfo> {
        if (this.tokenCache.has(reportId)) {
            return of(this.tokenCache.get(reportId));
        }

        return this.httpClient.get<{ embedUrl: string }>(`${environment.powerBIConfig.baseUrl}/reports/${reportId}`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                contentType: "application/json"
            }
        }).pipe(
            retry({
                count: this.maxThrottleRetries,
                delay: (error, retryCount) => {
                    if (!(error instanceof HttpErrorResponse) || error.status !== 429) {
                        return throwError(() => error);
                    }
                    return timer(this.getRetryDelayMs(error, retryCount));
                }
            }),
            map(response => ({
                embedUrl: response.embedUrl,
                accessToken: accessToken
            }))
        );
    }

    private getRetryDelayMs(error: HttpErrorResponse, retryCount: number): number {
        const retryAfterHeader = error.headers.get("Retry-After");
        if (!retryAfterHeader) {
            return this.throttleDelay * retryCount;
        }

        const retryAfterSeconds = Number.parseInt(retryAfterHeader, 10);
        if (!Number.isNaN(retryAfterSeconds) && retryAfterSeconds >= 0) {
            return retryAfterSeconds * 1000;
        }

        const retryAtTimestamp = Date.parse(retryAfterHeader);
        if (!Number.isNaN(retryAtTimestamp)) {
            return Math.max(retryAtTimestamp - Date.now(), 0);
        }

        return this.throttleDelay * retryCount;
    }
}
